import { AlertCircle, X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useRef, useState } from 'react'

import { useCenterStore } from '@caradasstore/CenterStore'
import { extractApiError } from '@components/branded-modal'
import { ToggleActive } from '@components/buttons'
import { AdasAlert, AdasBox, AdasButton, AdasSearchMenu, AdasTextField, AdasSwitch, AdasTypography } from '@components/wrapper-components'
import { UsaStates } from 'usa-states'
import { updateClient, autoApplyClientRebate, toggleClientCccConnection } from '../../../api/api'
import { phoneNumberFormat, validations, zipCodeInput } from '../../../utils/utils'
import { LABEL_DISPLAY, MAX_CLIENT_NOTES_LENGTH, TEXT_CONSTANTS } from '../../../constants'
import { Logger } from '../../../logger'
import { useRebateInvoiceConfirm } from '../useRebateInvoiceConfirm'

const toRebateNumber = (value) => {
  const number = Number.parseFloat(value ?? '0')
  return Number.isNaN(number) ? 0 : number
}

export const ClientForm = (props) => {
  const { clientData = { is_active: true, type: 'FLEET' }, close, onSaveSuccess, existingClients = [] } = props
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const [client, setClient] = useState(clientData ? clientData : {})
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    address1: false,
    city: false,
    state: false,
    zip: false
  })
  const [valid, setValid] = useState({
    name: validations.name(clientData.name),
    email: validations.email(clientData.email),
    phone: validations.phone(clientData.phone),
    address1: validations.address1(clientData.address1),
    city: validations.city(clientData.city),
    state: validations.state(clientData.state),
    zip: validations.zip(clientData.zip)
  })
  const [duplicateName, setDuplicateName] = useState(false)
  const [apiError, setApiError] = useState(null)
  const { confirmRebateChange, rebateConfirmModal } = useRebateInvoiceConfirm()
  const emailInputRef = useRef()
  const usStates = new UsaStates()

  const formatInputValue = (name, value) => {
    switch (name) {
      case 'zip':
        return zipCodeInput(value)
      case 'email':
        return value.replace(/\s/g, '')
      case 'vehicle_count':
        return value.replace(/\D/g, '')
      case 'rebate_percent': {
        const numeric = value.replace(/[^\d.]/g, '')
        if (numeric === '') return ''
        if (/^\d{1,2}(\.\d{0,2})?$/.test(numeric)) return numeric
        return null // Invalid pattern — ignore update
      }
      default:
        return value
    }
  }
  const updateStates = (name, value) => {
    setClient((prev) => ({ ...prev, [name]: value }))
    setTouched((prev) => ({ ...prev, [name]: true }))
    setValid((prev) => {
      const updated = { ...prev }
      if (validations[name]) {
        updated[name] = validations[name](value)
      }
      return updated
    })
  }

  const handleInputChange = useCallback((e) => {
    const { name, value: rawValue } = e.target
    if (name === 'name') {
      setDuplicateName(false)
    }
    const formattedValue = formatInputValue(name, rawValue)
    if (formattedValue === null) return

    updateStates(name, formattedValue)
  }, [])

  const handleRebateBlur = (e) => {
    const val = e.target.value.trim()
    if (val === '') {
      setClient((prev) => ({
        ...prev,
        rebate_percent: '0.00'
      }))
    }
  }

  const handleToggleActive = useCallback((e) => {
    const value = e.target.value
    setClient((prev) => {
      const client = {
        ...prev,
        is_active: value === 'active'
      }
      return client
    })
  }, [])

  const trimSpaces = (data) => {
    const trimmedData = {}
    for (const key in data) {
      if (typeof data[key] === 'string') {
        trimmedData[key] = data[key].trim()
      } else {
        trimmedData[key] = data[key]
      }
    }
    return trimmedData
  }
  const rebate_value = client && client.rebate_percent != null && client?.rebate_percent !== 0 ? client.rebate_percent : '0.00'

  const handleAutoApplyRebateToggle = useCallback(
    async (e) => {
      const checked = e.target.checked
      const normalizeRebateNumber = (value) => {
        const number = Number.parseFloat(value ?? '0')
        return Number.isNaN(number) ? 0 : Number(number.toFixed(2))
      }

      // Toggling rewrites the rebate on the client's open invoices — warn before switching.
      if (client?.id && !(await confirmRebateChange(client.id, checked ? 'enable' : 'disable'))) {
        return
      }

      // Update local state immediately for better UX
      setClient((prev) => ({
        ...prev,
        auto_apply_rebate: checked
      }))

      // Only call API if client has an ID (existing client, not new)
      if (client?.id) {
        try {
          const response = await autoApplyClientRebate({
            data: {
              id: client.id,
              rebate_percent: normalizeRebateNumber(rebate_value)
            }
          })
          const data = trimSpaces({
            ...client,
            auto_apply_rebate: checked
          })

          if (response && response.status === 200) {
            onSaveSuccess(data)
          }
        } catch (error) {
          // Revert state on error
          setClient((prev) => ({
            ...prev,
            auto_apply_rebate: !checked
          }))
          Logger.error({
            message: error,
            payload: { file: 'clientForm/handleAutoApplyRebateToggle', clientId: client.id }
          })
        }
      }
    },
    [client, onSaveSuccess, rebate_value, confirmRebateChange]
  )

  // calibration_active (CAR-3700) may be absent on stale payloads — treat missing as enabled.
  const calibrationActive = client?.calibration_active !== false

  const handleCccConnectionToggle = useCallback(
    async (e) => {
      const checked = e.target.checked

      setClient((prev) => ({
        ...prev,
        calibration_active: checked
      }))

      // Only call API if client has an ID (existing client, not new)
      if (client?.id) {
        try {
          const response = await toggleClientCccConnection({
            data: {
              id: client.id,
              calibration_active: checked
            }
          })
          const data = trimSpaces({
            ...client,
            calibration_active: checked
          })

          if (response && response.status === 200) {
            onSaveSuccess(data)
          }
        } catch (error) {
          // Revert state on error
          setClient((prev) => ({
            ...prev,
            calibration_active: !checked
          }))
          Logger.error({
            message: error,
            payload: { file: 'clientForm/handleCccConnectionToggle', clientId: client.id }
          })
        }
      }
    },
    [client, onSaveSuccess]
  )

  const handleSaveClient = useCallback(async () => {
    // Preserve the client's own calibration_center_id when editing from a global context
    // (e.g., the Global Clients page may surface a client whose center isn't the active one).
    // Fall back to the active center for the add-client flow.
    const data = trimSpaces({
      ...client,
      calibration_center_id: client?.calibration_center_id ?? centerDetail.id,
      country: 'USA'
    })

    if (existingClients.includes(client.name)) {
      setValid({ ...valid, name: false })
      setDuplicateName(true)
      return
    }
    // A new percent on a client that auto-applies its rebate rewrites its open invoices.
    const rebatePercentChanged =
      client?.id && client.auto_apply_rebate && toRebateNumber(clientData?.rebate_percent) !== toRebateNumber(client.rebate_percent)
    if (rebatePercentChanged && !(await confirmRebateChange(client.id, 'percent'))) {
      return
    }
    setApiError(null)
    try {
      const response = await updateClient({ data })
      if (response && response.status === 200) {
        setClient(response.data)
        close()
        onSaveSuccess(data)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'clientForm/handleSaveClient', data } })
      setApiError(extractApiError(error) || 'Failed to save client. Please try again.')
    }
  }, [centerDetail.id, client, clientData, close, confirmRebateChange, existingClients, onSaveSuccess, valid])

  const isDisabled = !valid.name || !valid.email || !valid.phone || !valid.address1 || !valid.city || !valid.state || !valid.zip
  const rebatePercentNumber = Number.parseFloat(rebate_value ?? '0')
  const hasRebate = rebatePercentNumber > 0
  const hasCccId = Boolean(String(client?.ccc_client_id ?? '').trim())
  return (
    <>
      {apiError && (
        <div
          role='alert'
          className='mb-4 flex items-start gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#B91C1C]'
        >
          <AlertCircle className='mt-0.5 h-4 w-4 flex-none' />
          <div className='flex-1 leading-snug'>{apiError}</div>
          <button
            type='button'
            onClick={() => setApiError(null)}
            aria-label='Dismiss error'
            className='ml-1 inline-flex h-5 w-5 flex-none items-center justify-center rounded text-[#B91C1C] hover:bg-[#FECACA]'
          >
            <X className='h-3.5 w-3.5' />
          </button>
        </div>
      )}
      <AdasTypography className='mb-2 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Client Type</AdasTypography>
      <AdasBox className='mb-5 inline-flex rounded-md border border-[#D1D5D8] bg-white p-0.5'>
        {[
          { label: 'Fleet', value: 'FLEET' },
          { label: 'Customer Pay', value: 'CUSTOMERPAY' }
        ].map((btn) => {
          const active = client?.type === btn.value
          return (
            <button
              key={btn.value}
              type='button'
              onClick={() => handleInputChange({ target: { name: 'type', value: btn.value } })}
              className={`rounded px-5 py-1.5 text-[13px] font-bold uppercase tracking-wide transition-colors ${
                active ? 'bg-[#0A1628] text-white' : 'bg-transparent text-[#0A1628] hover:bg-[#F8FAFC]'
              }`}
            >
              {btn.label}
            </button>
          )
        })}
      </AdasBox>
      <AdasTextField
        onChange={handleInputChange}
        name='name'
        className='mb-4'
        id='name'
        label='Name'
        required
        variant='outlined'
        helperText={duplicateName ? 'Client already exists, please review Client list' : 'Maximum 40 characters'}
        value={client && client.name ? client.name : ''}
        error={touched.name && !valid.name}
        fullWidth={true}
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 40 }}
      />
      <AdasTextField
        onChange={handleInputChange}
        name='vehicle_count'
        className='mb-4'
        id='vehicle-count'
        label={LABEL_DISPLAY.POTENTIAL_MONTHLY_RO_COUNT_LABEL}
        variant='outlined'
        value={client && client.vehicle_count}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          maxLength: 3,
          inputMode: 'numeric',
          pattern: '\\d*'
        }}
      />
      <AdasTextField
        onChange={handleInputChange}
        name='rebate_percent'
        className='mb-4'
        id='rebate-percent'
        label={LABEL_DISPLAY.REBATE_OR_DISCOUNT_PERCENT_LABEL}
        variant='outlined'
        value={rebate_value}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        onBlur={handleRebateBlur}
        InputProps={{
          endAdornment: <span style={{ marginLeft: 4 }}>%</span>
        }}
        inputProps={{
          maxLength: 8,
          inputMode: 'decimal',
          pattern: '^\\d{1,2}(\\.\\d{1,2})?%?$'
        }}
      />
      <AdasBox className='mb-4 flex items-center justify-between rounded-lg border border-[#EBEDF0] bg-[#F8FAFC] px-3 py-2.5'>
        <AdasTypography className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>
          Auto-apply rebate to invoices
        </AdasTypography>
        <AdasSwitch
          checked={Boolean(client?.auto_apply_rebate)}
          onChange={handleAutoApplyRebateToggle}
          name='auto_apply_rebate'
          disabled={!hasRebate}
        />
      </AdasBox>
      <AdasBox className='mb-4'>
        <ToggleActive toggle={client.is_active} handleToggleActive={handleToggleActive} />
      </AdasBox>
      <AdasTextField
        onChange={handleInputChange}
        name='phone'
        className='mb-4'
        id='phone'
        label='Phone *'
        variant='outlined'
        value={client && client.phone ? phoneNumberFormat(client.phone) : ''}
        error={touched.phone && !valid.phone}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          maxLength: 14
        }}
      />
      <AdasTextField
        type='text'
        onChange={handleInputChange}
        inputRef={emailInputRef}
        name='email'
        className='mb-4'
        id='email'
        label='Email *'
        variant='outlined'
        value={client && client.email ? client.email : ''}
        error={touched.email && !valid.email}
        helperText={touched.email && !valid.email ? TEXT_CONSTANTS.ENTER_VALID_EMAIL : ''}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          maxLength: 70
        }}
      />
      <AdasTextField
        onChange={handleInputChange}
        name='address1'
        className='mb-4'
        id='address'
        label='Address *'
        variant='outlined'
        value={client && client.address1 ? client.address1 : ''}
        error={touched.address1 && !valid.address1}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          maxLength: 40
        }}
      />
      <AdasTextField
        onChange={handleInputChange}
        name='city'
        className='mb-4'
        id='city'
        label='City *'
        variant='outlined'
        value={client && client.city ? client.city : ''}
        error={touched.city && !valid.city}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          maxLength: 28
        }}
      />
      <AdasSearchMenu
        fullWidth
        id='select-state'
        select
        label='State *'
        variant='outlined'
        onChange={handleInputChange}
        className='mb-4'
        name='state'
        value={client && client.state ? client.state : ''}
        error={touched.state && !valid.state}
        options={usStates?.states?.map((state) => ({
          value: state.abbreviation,
          name: state.name
        }))}
      />
      <AdasTextField
        onChange={handleInputChange}
        name='zip'
        className='mb-4'
        id='zip'
        label='Zip *'
        variant='outlined'
        value={client && client.zip ? client.zip : ''}
        error={touched.zip && !valid.zip}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          maxLength: 5
        }}
      />
      <AdasSearchMenu
        onChange={handleInputChange}
        name='country'
        select
        className='mb-4'
        id='country'
        label='Country'
        variant='outlined'
        value={client && client.country ? client.country : 'USA'}
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
        inputProps={{
          maxLength: 25
        }}
        options={[{ value: 'USA', name: 'United States of America' }]}
      />
      <AdasTextField
        fullWidth
        name='notes'
        label='Additional Notes'
        className='mb-4'
        value={client.notes}
        onChange={handleInputChange}
        inputProps={{ maxLength: MAX_CLIENT_NOTES_LENGTH }}
        helperText={`Maximum ${MAX_CLIENT_NOTES_LENGTH} characters (${MAX_CLIENT_NOTES_LENGTH - (client.notes?.length || 0)} remaining).`}
        multiline
        rows={4}
      />
      <AdasTextField
        onChange={handleInputChange}
        name='ccc_client_id'
        className='mb-4'
        id='ccc-client-id'
        label='CCC ID'
        variant='outlined'
        helperText='External identifier used to reconcile this client with CCC.'
        value={client?.ccc_client_id || ''}
        fullWidth={true}
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 32 }}
      />

      <AdasBox className='mb-4 flex items-center justify-between rounded-lg border border-[#EBEDF0] bg-[#F8FAFC] px-3 py-2.5'>
        <AdasTypography className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>
          Accept CCC Secure Share Estimates
        </AdasTypography>
        <AdasSwitch checked={calibrationActive} onChange={handleCccConnectionToggle} name='calibration_active' disabled={!hasCccId} />
      </AdasBox>

      {duplicateName && <AdasAlert severity='error'>Client already exists, please review Client list</AdasAlert>}
      {rebateConfirmModal}
      <AdasBox className='mt-5 flex items-center justify-end gap-3'>
        <AdasButton variant='outlined' aria-label='cancel' color='primary' onClick={close} className='rounded-lg px-5'>
          CANCEL
        </AdasButton>
        <AdasButton
          type='submit'
          disabled={isDisabled}
          variant='contained'
          aria-label='save'
          color='primary'
          isDebounceEnabled={true}
          onClick={handleSaveClient}
          className='rounded-lg bg-[#0A1628] px-5 text-white hover:bg-[#1F2D3D]'
        >
          SAVE
        </AdasButton>
      </AdasBox>
    </>
  )
}

ClientForm.propTypes = {
  clientData: PropTypes.object,
  close: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  existingClients: PropTypes.array
}
